import React from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { validator as patientValidator } from "lib/build/forms/common/patient";
import { Schema, Value } from "lib/build/schema";

import { FullPagePreloader } from "src/common/components/Preloader";
import { api, getApiErrorData, isApiError } from "src/common/utils/api";
import { useConfirm } from "src/features/confirm";
import { useRefetchEvents } from "src/features/events";
import { FormState } from "src/features/forms";
import { mapLogItemsToEventQueries } from "src/features/forms/utils/mapLogItemsToEventQueries";
import { useCreateGenericError } from "src/features/notifications";
import { useOrgModParams } from "src/features/orgmod";
import { usePatientIdParam } from "src/features/patient";
import { genericMessages } from "src/messages/generic";
import {
	PatientForm,
	usePatientForm
} from "src/views/patient-form/PatientForm";
import {
	addMaxItems,
	removeSelectedOptions
} from "src/views/patient-form/utils";

const validator = Schema.form().properties({
	version: Schema.string().optional(),
	timestamp: Schema.dateTime().optional(),
	value: patientValidator
});

interface EditPatientFormValue {
	version: string;
	value: Value;
}

const messages = defineMessages({
	editPatient: {
		id: "form.edit_patient",
		defaultMessage: "Páciens szerkesztése"
	},
	concurrentError: {
		id: "form.edit_patient.concurrent_error",
		defaultMessage:
			"Ez a páciens már módosítva lett egy másik felhasználó által. Szeretné felülírni az előző mentést?"
	}
});

export const EditPatient: React.FunctionComponent = () => {
	const patientId = usePatientIdParam();
	const [isLoading, initialValue] = useInitialEditPatientFormValue(patientId);
	if (isLoading || initialValue === null) {
		return <FullPagePreloader />;
	}
	return <EditPatientForm id={patientId} initialValue={initialValue} />;
};

interface EditPatientFormProps {
	id: string;
	initialValue: EditPatientFormValue;
}

const EditPatientForm: React.FunctionComponent<EditPatientFormProps> = ({
	id,
	initialValue
}) => {
	const formProps = useEditPatientForm(id, initialValue);
	return (
		<PatientForm
			titleText={<FormattedMessage {...messages.editPatient} />}
			submitText={<FormattedMessage {...genericMessages.save} />}
			{...formProps}
		/>
	);
};

function useInitialEditPatientFormValue(
	id: string
): readonly [boolean, EditPatientFormValue | null] {
	const { org, mod } = useOrgModParams();

	const [isLoading, setLoading] = React.useState(true);
	const [value, setValue] = React.useState<EditPatientFormValue | null>(null);

	const createGenericError = useCreateGenericError();

	const fetchPatient = React.useCallback(async () => {
		setLoading(true);
		setValue(null);
		try {
			const { data } = await api.get<EditPatientFormValue[]>(
				`/patients/${id}?organization=${org}&module=${mod}&form=patient`
			);
			const [{ version, value }] = data;
			setLoading(false);
			setValue({ version, value });
		} catch {
			createGenericError();
		}
	}, [id, org, mod, createGenericError]);

	React.useEffect(() => {
		fetchPatient();
	}, [fetchPatient]);

	return [isLoading, value];
}

function useEditPatientForm(
	id: string,
	value: EditPatientFormValue
): {
	formState: FormState;
	isSubmitted: boolean;
	isLoading: boolean;
	onSubmit(): Promise<void>;
} {
	const navigate = useNavigate();
	const { formatMessage } = useIntl();

	const { org, mod } = useOrgModParams();

	const confirm = useConfirm();
	const createGenericError = useCreateGenericError();

	const rawFormState = usePatientForm(validator, value);
	const formState = useConnectedUserOptionOptimizations(rawFormState);

	const [{ isSubmitted, isLoading }, setState] = React.useState({
		isSubmitted: false,
		isLoading: false
	});

	const refetchEvents = useRefetchEvents(id);

	const onSubmit = async (): Promise<void> => {
		setState(state => ({ ...state, isSubmitted: true }));
		if (formState.errors.length === 0) {
			setState(state => ({ ...state, isLoading: true }));
			try {
				const {
					value: { version: currentVersion, value: currentValue }
				} = formState;
				const { status } = await api.put(
					`/patients/${id}?organization=${org}&module=${mod}&form=patient`,
					{
						version: currentVersion,
						value: currentValue
					}
				);
				if (status === 205) {
					navigate(`/${org}/${mod}/patients/list`);
				} else {
					for (const query of mapLogItemsToEventQueries(
						formState.messages
					)) {
						refetchEvents(query);
					}
					navigate(`/${org}/${mod}/patients/${id}/dashboard`);
				}
			} catch (error) {
				setState(state => ({ ...state, isLoading: false }));
				if (isApiError<string>(error)) {
					const { code, payload: newVersion } =
						getApiErrorData(error);
					if (code === "patients.updatePatient.failed.concurrent") {
						if (
							(await confirm({
								content: formatMessage(messages.concurrentError)
							})) === true
						) {
							formState.setValue(({ value }: Value) => ({
								version: newVersion,
								value
							}));
							onSubmit();
						}
					}
				} else {
					createGenericError();
				}
			}
		}
	};

	return {
		formState,
		isSubmitted,
		isLoading,
		onSubmit
	};
}

function useConnectedUserOptionOptimizations(formState: FormState): FormState {
	return React.useMemo(() => {
		const { value, messages: messagesA } = formState;
		const messagesB = addMaxItems(messagesA);
		const messagesC = removeSelectedOptions(value, messagesB);
		return messagesA === messagesC
			? formState
			: {
					...formState,
					value,
					messages: messagesC
				};
	}, [formState]);
}
